import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

const FILTERS = [
  { key: "all", label: "my_missions.filter_all" },
  { key: "draft", label: "my_missions.filter_draft" },
  { key: "open", label: "my_missions.filter_open" },
  { key: "shortlisting", label: "my_missions.filter_shortlisting" },
  { key: "filled", label: "my_missions.filter_filled" },
  { key: "closed", label: "my_missions.filter_closed" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function fmtTjm(m) {
  if (m.tjm_type === "fixed") return `$${m.tjm_min}`;
  return `$${m.tjm_min} – $${m.tjm_max}`;
}

const STATUS_CLASS = {
  draft: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
  open: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100",
  shortlisting: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100",
  filled: "bg-amber-50 text-amber-700 ring-1 ring-amber-100",
  closed: "bg-rose-50 text-rose-700 ring-1 ring-rose-100",
};

export default function MyMissions() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (q) params.q = q;
      const { data } = await api.get("/missions/mine", { params });
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally {
      setLoading(false);
    }
  }, [filter, q, t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="my-missions-page">
      <Toaster richColors position="top-right" />
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">
              {t(`dashboard.role_badge_${user?.role}`)}
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              {t("my_missions.title")}
            </h1>
            <p className="mt-2 text-sm text-slate-600">{t("my_missions.subtitle")}</p>
          </div>
          <Link
            to="/missions/new"
            className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
            data-testid="my-missions-new-btn"
          >
            {t("my_missions.new_cta")}
          </Link>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("my_missions.search_placeholder")}
            className="w-full max-w-xs rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            data-testid="my-missions-search"
          />
          <div className="flex flex-wrap gap-2" data-testid="my-missions-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`my-missions-filter-${f.key}`}
                className={
                  "rounded-full px-3 py-1.5 text-sm font-medium transition-colors " +
                  (filter === f.key
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100")
                }
              >
                {t(f.label)}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500 shadow-sm">
            {t("common.loading")}
          </div>
        )}
        {!loading && items.length === 0 && (
          <div
            className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm"
            data-testid="my-missions-empty"
          >
            <h3 className="text-lg font-semibold text-slate-900">{t("my_missions.empty_title")}</h3>
            <p className="mt-2 text-sm text-slate-600">{t("my_missions.empty_body")}</p>
            <Link
              to="/missions/new"
              className="mt-6 inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
              data-testid="my-missions-empty-cta"
            >
              {t("my_missions.new_cta")}
            </Link>
          </div>
        )}
        {!loading && items.length > 0 && (
          <ul className="grid gap-4" data-testid="my-missions-list">
            {items.map((m) => (
              <MissionRow key={m.id} mission={m} />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function MissionRow({ mission }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canPreview = mission.status === "open" || mission.status === "shortlisting";
  return (
    <li
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
      data-testid={`my-mission-row-${mission.id}`}
    >
      <div
        onClick={() => navigate(`/missions/${mission.id}`)}
        className="cursor-pointer"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 min-w-[240px]">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900 md:text-lg">{mission.title}</h3>
              <StatusPill status={mission.status} />
              {mission.visibility === "invite_only" && (
                <span className="rounded-full bg-fuchsia-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-fuchsia-700 ring-1 ring-fuchsia-100">
                  {t("mission.visibility_invite_only")}
                </span>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(mission.required_skills || []).slice(0, 6).map((s) => (
                <span key={s} className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                  {s}
                </span>
              ))}
            </div>
          </div>
          <div className="text-right text-xs text-slate-600">
            <div className="text-sm font-semibold text-slate-900">{fmtTjm(mission)}</div>
            <div className="mt-0.5">
              {t("mission.start")} {fmtDate(mission.start_date)} · {mission.duration_months} {t("mission.months")}
            </div>
            <div className="mt-0.5">
              {mission.location_city} · {t(`roster.remote_${mission.remote_policy}`)}
            </div>
          </div>
        </div>
      </div>
      {canPreview && (
        <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
          <p className="text-xs text-slate-500" data-testid={`my-mission-preview-hint-${mission.id}`}>
            {t("my_missions_extra.preview_hint")}
          </p>
          <Link
            to={`/marketplace/${mission.id}?preview=1`}
            onClick={(e) => e.stopPropagation()}
            className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
            data-testid={`my-mission-preview-btn-${mission.id}`}
          >
            {t("my_missions_extra.preview_buyer")} →
          </Link>
        </div>
      )}
    </li>
  );
}

function StatusPill({ status }) {
  const { t } = useTranslation();
  return (
    <span
      className={
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider " +
        (STATUS_CLASS[status] || STATUS_CLASS.draft)
      }
      data-testid={`mission-status-pill-${status}`}
    >
      {t(`mission.status_${status}`)}
    </span>
  );
}
